import React, { useEffect, useState, useRef, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Waves, Sun, Heart, Coffee, Sparkles, Calendar, MapPin, Clock, CheckCircle2, Users, X, User, Mail, Phone, Lock, Info } from 'lucide-react';
import { openTranzilaPayment } from '../utils/tranzila';
import { sendWomensSupRegistrationToMake } from '../utils/makeWebhook';
import { UtmCapture } from '../components/UtmCapture';
import { getUtmParams } from '../utils/utmTracking';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
// Open registration dates — keep in sync with the hero / details sections
const EVENT_DATES = [{
  value: '04.09.2026',
  label: 'שישי, 04.09.2026'
}];
export function WomensSUPMorningPage() {
  const PRICE = 480;
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  // ============================================
  // TEST MODE - DELETE THIS BLOCK TO REMOVE
  // Activate via URL: ?test_mode=womenssup2026
  // ============================================
  const [isTestMode, setIsTestMode] = useState(false);
  // ============================================
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    // Only one open date right now, so it starts selected
    selectedDate: EVENT_DATES[0].value,
    dateOfBirth: '',
    noMedicalLimitations: false,
    hasExperience: '',
    notes: ''
  });
  const paymentIframeRef = useRef<HTMLIFrameElement>(null);
  // Detect payment success — runs in both top window AND iframe context.
  // If we're inside an iframe at /womens-sup-morning?paid=1, send the
  // registration to Make.com (ONLY happens after a successful Tranzila payment)
  // and then notify the parent window so it can show the success state.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    // Detect success from EITHER our own marker OR Tranzila's response param.
    // Tranzila replaces our query string with its own params on redirect.
    const isPaid = urlParams.get('paid') === '1' || urlParams.get('Response') === '000';
    if (!isPaid) return;
    console.log('💳 Payment success detected on page load', {
      paid: urlParams.get('paid'),
      Response: urlParams.get('Response'),
      ConfirmationCode: urlParams.get('ConfirmationCode'),
      isInIframe: window.parent !== window
    });
    const sendWebhookAndNotify = async () => {
      // Read the registration data saved at form submission
      let registration: any = null;
      try {
        const stored = localStorage.getItem('womensSupMorningRegistration');
        if (stored) registration = JSON.parse(stored);
      } catch (err) {
        console.warn('Could not read registration from localStorage', err);
      }
      // Send to Make.com — only after a confirmed successful payment
      if (registration) {
        // Prefer UTM params stored with the registration (captured at submit
        // time, more reliable in Meta in-app browsers). Fall back to the
        // global UTM store if anything is missing.
        const fallbackUtm = getUtmParams();
        const utm = {
          utm_source: registration.utm_source || fallbackUtm.utm_source,
          utm_medium: registration.utm_medium || fallbackUtm.utm_medium,
          utm_campaign: registration.utm_campaign || fallbackUtm.utm_campaign,
          utm_content: registration.utm_content || fallbackUtm.utm_content,
          utm_term: registration.utm_term || fallbackUtm.utm_term,
          utm_id: registration.utm_id || fallbackUtm.utm_id,
          fbclid: registration.fbclid || fallbackUtm.fbclid
        };
        try {
          await sendWomensSupRegistrationToMake({
            type: 'Womens SUP Morning Registration',
            source: 'MagicPatterns',
            paid: true,
            event: registration.event || 'בוקר נשי - הכוח שבמים',
            eventDate: registration.eventDate || '04.09.2026',
            selectedDate: registration.selectedDate || registration.eventDate || '',
            fullName: registration.fullName || '',
            email: registration.email || '',
            phone: registration.phone || '',
            dateOfBirth: registration.dateOfBirth || '',
            noMedicalLimitations: !!registration.noMedicalLimitations,
            hasExperience: registration.hasExperience || '',
            notes: registration.notes || '',
            price: registration.price ?? '',
            submittedAt: registration.submittedAt || new Date().toISOString(),
            ...utm
          });
          // NOTE: wm_purchase pixel event is fired in the TOP WINDOW
          // (see message listener below), NOT here in the iframe — the
          // iframe is too short-lived for fbevents.js to finish loading
          // and dispatch the network call before the modal closes.
          // Clear stored data after a successful send
          localStorage.removeItem('womensSupMorningRegistration');
        } catch (err) {
          console.error('Webhook send failed', err);
        }
      }
      if (window.parent !== window) {
        // Inside the Tranzila iframe — tell the parent to show the success UI.
        // Include submittedAt so the parent can dedup wm_purchase even if it
        // already cleared localStorage.
        try {
          window.parent.postMessage({
            type: 'womens-sup-payment-success',
            submittedAt: registration?.submittedAt
          }, window.location.origin);
        } catch (err) {
          console.warn('Could not postMessage to parent', err);
        }
      } else {
        // Top-level visit with paid=1 (fallback). Show success directly.
        setPaymentSuccess(true);
        setIsPaymentOpen(true);
        window.history.replaceState({}, '', window.location.pathname);
      }
    };
    sendWebhookAndNotify();
  }, []);
  // Top window: listen for the success message from the iframe.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'womens-sup-payment-success') {
        setPaymentSuccess(true);
        setIsSubmitting(false);
        // Meta Pixel — fire wm_purchase custom event HERE in the top window.
        // The top window has had the full app loaded for the entire session,
        // so fbevents.js is fully initialized and the call will dispatch
        // a proper /tr/ network request with all parameters.
        // Dedup on submittedAt to avoid double-firing on remounts.
        try {
          const stored = localStorage.getItem('womensSupMorningRegistration');
          const reg = stored ? JSON.parse(stored) : null;
          const submittedAt = reg?.submittedAt || event.data?.submittedAt || '';
          const dedupKey = `wm_purchase_fired_${submittedAt}`;
          const alreadyFired = submittedAt && localStorage.getItem(dedupKey);
          if (!alreadyFired && typeof window !== 'undefined' && (window as any).fbq) {
            ;
            (window as any).fbq('trackCustom', 'wm_purchase', {
              content_name: 'בוקר נשי - הכוח שבמים',
              content_category: 'Womens SUP Morning',
              value: reg?.price ?? PRICE,
              currency: 'ILS',
              utm_source: reg?.utm_source,
              utm_medium: reg?.utm_medium,
              utm_campaign: reg?.utm_campaign,
              utm_content: reg?.utm_content,
              utm_term: reg?.utm_term,
              utm_id: reg?.utm_id,
              fbclid: reg?.fbclid
            });
            if (submittedAt) localStorage.setItem(dedupKey, '1');
          }
        } catch (err) {
          console.warn('Could not fire wm_purchase pixel event', err);
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
  // ============================================
  // TEST MODE - DELETE THIS BLOCK TO REMOVE
  // ============================================
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const testModeParam = urlParams.get('test_mode');
    if (testModeParam === 'womenssup2026') {
      setIsTestMode(true);
      localStorage.setItem('womens_test_mode_active', 'true');
      console.log('🧪 WOMENS SUP TEST MODE ACTIVATED - Charge will be ₪1');
    } else {
      const stored = localStorage.getItem('womens_test_mode_active');
      if (stored === 'true') {
        setIsTestMode(true);
        console.log('🧪 WOMENS SUP TEST MODE ACTIVE (from localStorage)');
      }
    }
  }, []);
  // ============================================
  const openForm = () => {
    setPaymentSuccess(false);
    setIsFormOpen(true);
    // Meta Pixel — track form opened (AddToCart equivalent)
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;
      (window as any).fbq('track', 'AddToCart', {
        content_name: 'בוקר נשי - הכוח שבמים',
        content_category: 'Womens SUP Morning',
        value: PRICE,
        currency: 'ILS'
      });
    }
  };
  const closeForm = () => {
    if (!isSubmitting) setIsFormOpen(false);
  };
  const closePayment = () => {
    setIsPaymentOpen(false);
    setIsSubmitting(false);
    setPaymentSuccess(false);
  };
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName.trim() || !formData.email.trim() || !formData.phone.trim() || !formData.selectedDate || !formData.dateOfBirth || !formData.noMedicalLimitations || !formData.hasExperience) return;
    setIsSubmitting(true);
    // ============================================
    // TEST MODE - DELETE THIS BLOCK TO REMOVE
    // ============================================
    const chargeSum = isTestMode ? 1 : PRICE;
    // ============================================
    // Capture UTM params NOW at form submission time so they survive any
    // localStorage clearing that might happen in Meta in-app browsers.
    const utmParams = getUtmParams();
    console.log('📊 Capturing UTM params at form submission:', utmParams);
    // Persist registration for later record keeping / thank you page
    try {
      localStorage.setItem('womensSupMorningRegistration', JSON.stringify({
        ...formData,
        event: 'בוקר נשי - הכוח שבמים',
        eventDate: formData.selectedDate,
        price: chargeSum,
        submittedAt: new Date().toISOString(),
        // UTM params stored alongside registration
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium,
        utm_campaign: utmParams.utm_campaign,
        utm_content: utmParams.utm_content,
        utm_term: utmParams.utm_term,
        utm_id: utmParams.utm_id,
        fbclid: utmParams.fbclid
      }));
    } catch (err) {
      console.warn('Could not persist registration to localStorage', err);
    }
    // Track Meta Pixel event if available
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;
      (window as any).fbq('track', 'InitiateCheckout', {
        content_name: 'בוקר נשי - הכוח שבמים',
        content_category: 'Womens SUP Morning',
        value: chargeSum,
        currency: 'ILS'
      });
    }
    // Close form modal and open the payment modal with embedded iframe
    setIsFormOpen(false);
    setIsPaymentOpen(true);
    // Defer to ensure the iframe (name="tranzila") is in the DOM before submitting the form
    setTimeout(() => {
      openTranzilaPayment({
        sum: chargeSum,
        contact: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        pdesc: `בוקר נשי - הכוח שבמים (${formData.selectedDate})`,
        success_url: `${window.location.origin}/womens-sup-morning?paid=1`,
        fail_url: window.location.href
      });
    }, 150);
  };
  // If we're loaded inside the Tranzila iframe at /womens-sup-morning?paid=1,
  // skip rendering the full landing page. The iframe is short-lived and only
  // needs to send the webhook + postMessage to the parent. Showing the full
  // page would briefly flash all the marketing content inside the modal.
  const isPaidIframe = typeof window !== 'undefined' && window.parent !== window && (new URLSearchParams(window.location.search).get('paid') === '1' || new URLSearchParams(window.location.search).get('Response') === '000');
  if (isPaidIframe) {
    return <div dir="rtl" className="font-['Heebo'] min-h-screen bg-rose-50 flex items-center justify-center text-stone-700" data-id="element-2526" data-mp="1valdligi6rm1">
        <div className="text-center" data-id="element-2527" data-mp="257zutka93kfe">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full border-4 border-rose-200 border-t-rose-500 animate-spin" data-id="element-2528" data-mp="256y6ie4851fq" />
          <p className="text-base font-medium" data-id="element-2529" data-mp="208dg16erxw4h">מאשרת את ההרשמה…</p>
        </div>
      </div>;
  }
  return <div dir="rtl" className="font-['Heebo'] bg-rose-50 text-stone-800 min-h-screen overflow-x-hidden" data-id="element-2530" data-mp="2ewzsn0d0o9s0">
      {/* Silent UTM capture for Meta Ads attribution */}
      <UtmCapture data-id="element-2531" data-mp="2nz95vxzo6h93" />

      {/* ============================================ */}
      {/* TEST MODE INDICATOR - DELETE THIS TO REMOVE */}
      {/* ============================================ */}
      {isTestMode && <div className="fixed top-4 right-4 z-[80] bg-yellow-400 text-stone-900 px-4 py-2 rounded-lg shadow-lg font-bold text-sm flex items-center gap-2" data-id="element-2532" data-mp="h84goo7e7uok">
          <Sparkles className="w-4 h-4" data-id="element-2533" data-mp="qzs2a8h9qhi4" />
          TEST MODE - ₪1
        </div>}
      {/* ============================================ */}

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden py-28 md:py-24" data-id="element-2534" data-mp="3uh0gmicsez83">
        <div className="absolute inset-0 z-0" data-id="element-2535" data-mp="39xtc202ld35">
          <img src="https://cdn.magicpatterns.com/uploads/7UQXFa8QVzGYPRJjgEmh4j/c61b37f1-fb9e-4cd8-b43e-2e77a03ab830_(1).jpg" alt="בוקר נשי על הסאפ בכנרת" className="w-full h-full object-cover" data-id="element-2536" data-mp="3apml6672yq9" />
          <div className="absolute inset-0 bg-gradient-to-b from-rose-900/30 via-rose-800/40 to-stone-900/70" data-id="element-2537" data-mp="1irprdgw2zeop"></div>
        </div>

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto mt-16" data-id="element-2538" data-mp="1nozo7hlsusmk">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8
        }} data-id="element-2539" data-mp="1xuhhgm3rp8b9">
            {/* Sold-out dates — August now sold out too */}
            <div className="inline-flex flex-col items-center justify-center gap-3 py-4 px-6 sm:px-8 rounded-3xl bg-rose-500/30 text-white border-2 border-rose-300/60 mb-4 backdrop-blur-md shadow-2xl shadow-rose-900/40" data-id="element-2540" data-mp="1k0qgw380kyro">
              <span className="text-xs sm:text-sm uppercase tracking-[0.2em] text-rose-200/80 font-semibold" data-id="element-2541" data-mp="155ka3e13gvxa">
                המפגשים הקרובים אזלו
              </span>
              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3" data-id="element-2542" data-mp="115ryesgem9np">
                <span className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded-full bg-stone-900/40 text-stone-200 border border-stone-500/40 text-base sm:text-lg font-semibold backdrop-blur-sm relative overflow-hidden" data-id="element-2543" data-mp="2zj1puev9cohr">
                  <Calendar className="w-4 h-4" data-id="element-2544" data-mp="sthtmq9ndcsz" />
                  <span className="line-through opacity-70" data-id="element-2545" data-mp="xn2uqr6jemwe">03.07.2026</span>
                  <span className="absolute inset-0 flex items-center justify-center bg-stone-900/60 font-bold text-rose-400 tracking-wider text-xs uppercase" style={{
                  transform: 'rotate(-5deg)'
                }} data-id="element-2546" data-mp="jv67p1jq736v">
                    SOLD OUT
                  </span>
                </span>
                <span className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded-full bg-stone-900/40 text-stone-200 border border-stone-500/40 text-base sm:text-lg font-semibold backdrop-blur-sm relative overflow-hidden" data-id="element-2547" data-mp="12l4fdgjm2u9z">
                  <Calendar className="w-4 h-4" data-id="element-2548" data-mp="3nb3jtg1g6l7u" />
                  <span className="line-through opacity-70" data-id="element-2549" data-mp="3ndfynkbwsfgo">07.08.2026</span>
                  <span className="absolute inset-0 flex items-center justify-center bg-stone-900/60 font-bold text-rose-400 tracking-wider text-xs uppercase" style={{
                  transform: 'rotate(-5deg)'
                }} data-id="element-2550" data-mp="3sc10tpaq197r">
                    SOLD OUT
                  </span>
                </span>
              </div>
            </div>

            {/* Remaining spots — only one open date left */}
            <div className="inline-flex flex-col items-center justify-center gap-2 py-4 px-6 sm:px-8 rounded-3xl bg-emerald-500/25 text-white border-2 border-emerald-300/60 mb-6 backdrop-blur-md shadow-2xl shadow-emerald-900/30" data-id="element-2551" data-mp="3126b2r4blxxp">
              <span className="inline-flex items-center gap-2 text-lg sm:text-2xl font-extrabold" data-id="element-2552" data-mp="3auc1hwexc7as">
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" data-id="element-2553" data-mp="360pvuo76h3oh" />
                נותרו מקומות בודדים לבוקר נשי ב-4.9.2026
              </span>
              <span className="text-sm sm:text-base text-emerald-50/90 font-medium" data-id="element-2554" data-mp="2ddn2sbl9tl5m">
                תאריך באוקטובר יפורסם בקרוב
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-4 leading-tight" data-id="element-2555" data-mp="1rw528jn7cxzx">
              בוקר נשי - <span className="text-rose-300" data-id="element-2556" data-mp="1wuyde5f0ocwx">הכוח שבמים</span>
            </h1>
            <p dir="ltr" className="text-2xl md:text-4xl text-rose-100 mb-6 font-light italic tracking-wide" data-id="element-2557" data-mp="21qwxvhx85as2">
              Come Sup With Me
            </p>
            <p className="text-xl md:text-2xl text-rose-50 mb-10 font-light max-w-2xl mx-auto leading-relaxed" data-id="element-2558" data-mp="2asmowmn727pb">
              בואי ככה, בדיוק כמו שאת. כל השאר – עליי.
            </p>
            <button type="button" onClick={openForm} className="group inline-flex items-center justify-center gap-2.5 bg-rose-500 hover:bg-rose-600 text-white text-base sm:text-lg font-bold py-4 px-8 sm:px-10 rounded-full transition-all duration-200 shadow-xl shadow-rose-900/40 hover:shadow-2xl hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus-visible:ring-4 focus-visible:ring-rose-300/60" data-id="element-2559" data-mp="2kk0wlhpaudtj">
              <Sparkles className="w-5 h-5 shrink-0" data-id="element-2560" data-mp="it0krp2mw5v0" />
              <span data-id="element-2561" data-mp="ivmw7zsnb5ii">הרשמה ותשלום לתאריכים הבאים</span>
              <span className="transition-transform duration-200 group-hover:-translate-x-1" data-id="element-2562" data-mp="dtnt8r0tiam3">
                ←
              </span>
            </button>
          </motion.div>
        </div>

        <motion.div animate={{
        y: [0, 10, 0]
      }} transition={{
        repeat: Infinity,
        duration: 2
      }} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70 z-10" data-id="element-2563" data-mp="16jmmkym1otwk">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center p-1" data-id="element-2564" data-mp="wqyrq9eml00h">
            <div className="w-1.5 h-1.5 bg-white rounded-full" data-id="element-2565" data-mp="1p9m4n1jgx8qh"></div>
          </div>
        </motion.div>
      </section>

      {/* Invitation / Personal Note */}
      <section className="py-24 px-4 bg-white" data-id="element-2566" data-mp="1khb5w20grszi">
        <div className="max-w-3xl mx-auto text-center" data-id="element-2567" data-mp="225upin03ed32">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} data-id="element-2568" data-mp="227fjobihvu0e">
            <div className="inline-flex items-center gap-2 mb-6 text-rose-500" data-id="element-2569" data-mp="1s9rl1ahrq2cy">
              <Heart className="w-5 h-5" data-id="element-2570" data-mp="1o8fv13uzff48" />
              <span className="text-sm font-semibold tracking-wider uppercase" data-id="element-2571" data-mp="2kx5bcpqynzo3">
                ההזמנה שלי אלייך
              </span>
              <Heart className="w-5 h-5" data-id="element-2572" data-mp="2fyu5sr7p8bbo" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-8 leading-tight" data-id="element-2573" data-mp="2b6apvnljh43p">
              לפעמים את צריכה רק בוקר אחד.
            </h2>
            <div className="space-y-5 text-lg md:text-xl text-stone-600 leading-relaxed" data-id="element-2574" data-mp="274yxxbf706sc">
              <p data-id="element-2575" data-mp="33oodkud5v9p2">
                בוקר אחד שבו את מתנתקת מהיומיום, מתמסרת, מתחברת אל הטבע, ומרגישה
                את הכוח חוזר אלייך.
              </p>
              <p className="font-medium text-stone-800" data-id="element-2576" data-mp="leae5x4slxyt">
                בלי ניסיון קודם. בלי שיפוטיות. רק את, המים וקבוצת נשים בדיוק
                כמוך.
              </p>
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} className="mt-14 rounded-3xl overflow-hidden shadow-xl shadow-rose-200/40 border border-rose-100" data-id="element-2577" data-mp="ueozgwhrbd2b">
              <img src="https://cdn.magicpatterns.com/uploads/pvDjVs7yuSgf7363qaT2WF/IMG_8179_(1).jpg" alt="קבוצת נשים בבוקר נשי בחוף מעגן" className="w-full h-auto object-cover" data-id="element-2578" data-mp="1i405asdyrxnb" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* About Adi */}
      <section className="py-24 px-4 bg-gradient-to-b from-white to-rose-50" data-id="element-2579" data-mp="3o6dkuk99cybs">
        <div className="max-w-3xl mx-auto" data-id="element-2580" data-mp="3j6r8ed197m41">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="bg-white rounded-3xl shadow-xl shadow-rose-100/50 border border-rose-100 p-8 md:p-12" data-id="element-2581" data-mp="3j82ckdym2vvo">
            <div className="inline-flex items-center gap-2 mb-5 text-rose-500" data-id="element-2582" data-mp="6ouc4xd80jvw">
              <Heart className="w-4 h-4" data-id="element-2583" data-mp="6r6uhbmw4w9h" />
              <span className="text-xs font-semibold tracking-wider uppercase" data-id="element-2584" data-mp="kovhap0iqdh6">
                מי שמחכה לך שם
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-6 leading-tight" data-id="element-2585" data-mp="fnfsqmz4tou1">
              שלום, אני עדי
            </h2>

            <div className="space-y-4 text-stone-600 text-base md:text-lg leading-relaxed" data-id="element-2586" data-mp="2hhf1p1os7u3k">
              <p data-id="element-2587" data-mp="wybb05wi7aio">
                גיליתי את עולם הסאפ בשנים האחרונות וזה שינה אותי מבחוץ ומבפנים.
                התמכרתי - ואני מזמינה גם אותך לחולל בעצמך שינוי אמיתי, כזה שיעיר
                ויעורר, ירגש ויסחוף.
              </p>
              <p data-id="element-2588" data-mp="n89zsep91ml6">
                ההתמודדות עם הטבע, השקט והתנועה המתמדת על המים אפשרה לי להתחבר
                לקול הפנימי שלי ולגלות בי עוצמות שלא הכרתי.
              </p>
              <p className="font-semibold text-stone-800 text-lg md:text-xl pt-2" data-id="element-2589" data-mp="e83omzh0wyp6">
                מזמינה גם אותך לגלות בעצמך עולמות חדשים. 🌊
              </p>
            </div>

            <div className="mt-8 mb-2 flex items-center gap-3" data-id="element-2590" data-mp="4afgn7uepup2">
              <div className="h-px bg-rose-200 flex-1" data-id="element-2591" data-mp="4c0aswct7bme"></div>
              <span className="text-rose-400 text-2xl" style={{
              fontFamily: '"Brush Script MT", cursive'
            }} data-id="element-2592" data-mp="98h2u1n4qa4r">
                Adi
              </span>
              <div className="h-px bg-rose-200 flex-1" data-id="element-2593" data-mp="23mi8p6h3jxec"></div>
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} className="mt-10 rounded-2xl overflow-hidden shadow-lg shadow-rose-200/40 border border-rose-100" data-id="element-2594" data-mp="3jqu9yxciace">
              <img src="https://cdn.magicpatterns.com/uploads/o8NBipbPs9TUtEVJiiwzvh/image.png" alt="עדי על קרש סאפ בכנרת" className="w-full h-auto object-cover" data-id="element-2595" data-mp="3bzso26rpot56" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* What's in the morning */}
      <section className="py-24 px-4 bg-gradient-to-b from-rose-50 to-orange-50" data-id="element-2596" data-mp="2y0axbluuv5t6">
        <div className="max-w-6xl mx-auto" data-id="element-2597" data-mp="2t2j48oih5dne">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-2598" data-mp="2iy2lnndm1ifw">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4" data-id="element-2599" data-mp="2sx0cwy2f7q2l">
              מה מחכה לך בבוקר הזה?
            </h2>
            <div className="w-24 h-1 bg-rose-400 mx-auto rounded-full" data-id="element-2600" data-mp="207tbnrbl0mdn"></div>
          </motion.div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2601" data-mp="256edfv6hfe47">
            {[{
            image: 'https://cdn.magicpatterns.com/uploads/hfUVkEZQekPyb43CZMktV3/GetAttachmentThumbnail_(7).jpg',
            title: 'מעגל פתיחה',
            desc: 'כוס קפה חם, היכרות עדינה והכוונה לבוקר שלנו.'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/iZHHfKbDuzWiZmbvcXG53T/IMG_8165.jpg',
            title: 'סאפ בכנרת',
            desc: 'ממש מאפס - היכרות עם מבנה הסאפ והמשוט, טכניקת חתירה, וחתירה במים.'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/sFwj9YB7rvdyCPXtDrtWRm/IMG_5724.jpg',
            title: 'בראנץ׳ נשי',
            desc: 'ארוחת בוקר מפנקת, יין טוב, שיחה אמיתית וסגירת מעגל.'
          }, {
            image: 'https://cdn.magicpatterns.com/uploads/eom8E644yCYiU5JAxE9kj9/IMG_5754.jpg',
            title: 'נשימות ואמבטיית קרח',
            desc: 'הדרכת נשימות מעמיקה וטבילה מעצימה במי קרח מול הנוף.'
          }].map((item: any, idx) => {
            const Icon = item.icon;
            return <motion.div key={idx} variants={fadeInUp} className="bg-white p-8 rounded-3xl text-center hover:shadow-xl transition-shadow border border-rose-100 flex flex-col items-center" data-id="element-2602" data-mp="2je3z4v24hx1k" data-mpi={idx}>
                  {item.image ? <div className="w-20 h-20 rounded-full overflow-hidden mb-5 ring-4 ring-rose-100 shadow-md" data-id="element-2603" data-mp="2eeit60ojcofc">
                      <img src={item.image} alt={item.title} className="w-full h-full object-cover" data-id="element-2604" data-mp="3266aprzwqn4y" />
                    </div> : <div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mb-5" data-id="element-2605" data-mp="2taqi1eaw2e0y">
                      <Icon className="w-8 h-8 text-rose-500" data-id="element-2606" data-mp="3l3zdmuaj6x2c" />
                    </div>}
                  <h3 className="text-xl font-bold mb-3 text-stone-800" data-id="element-2607" data-mp="3puza5yfqnd4b">
                    {item.title}
                  </h3>
                  <p className="text-stone-600 leading-relaxed" data-id="element-2608" data-mp="314l84ab5b7no">{item.desc}</p>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </section>

      {/* Ice Bath & Breathing Section */}
      <section className="py-24 px-4 bg-white overflow-hidden" data-id="element-2609" data-mp="2rbvylkz5o3uz">
        <div className="max-w-6xl mx-auto" data-id="element-2610" data-mp="2mfyh3bc5orgm">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-2611" data-mp="2md2irlwec0rn">
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} data-id="element-2612" data-mp="3k25mgngaanu1">
              <div className="inline-flex items-center gap-2 mb-4 text-rose-500" data-id="element-2613" data-mp="1ypkwg5vwhuw2">
                <Sparkles className="w-5 h-5" data-id="element-2614" data-mp="1tpq9gdf8xpga" />
                <span className="text-sm font-semibold tracking-wider uppercase" data-id="element-2615" data-mp="1towxmy1fk9ra">
                  העצמה דרך הגוף
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-6 leading-tight" data-id="element-2616" data-mp="e6qukucxlcfa">
                הכוח שבנשימה ובקור
              </h2>

              <div className="space-y-6 text-lg text-stone-600 leading-relaxed" data-id="element-2617" data-mp="j4abk210k3ax">
                <p data-id="element-2618" data-mp="nyo9ab35emir">
                  <strong className="text-stone-800" data-id="element-2619" data-mp="nz6gu3v9gn5z">
                    חשיבות מערכת הנשימה:
                  </strong>{' '}
                  הנשימה היא העוגן שלנו. הדרכת הנשימות שנחווה יחד תלמד אותנו איך
                  להשתמש בנשימה כדי לווסת את מערכת העצבים, להרגיע את הגוף
                  ולהחזיר את השליטה לידיים שלנו.
                </p>
                <p data-id="element-2620" data-mp="16w7o5vcaezd3">
                  <strong className="text-stone-800" data-id="element-2621" data-mp="16umpbtixwu9f">טבילה במי קרח:</strong>{' '}
                  מעבר ליתרונות הפיזיים המוכחים כמו חיזוק המערכת החיסונית, הפחתת
                  דלקתיות ושיפור זרימת הדם — הטבילה בקרח היא אימון מנטלי טהור.
                  היא מפגישה אותנו עם קושי קיצוני ומלמדת אותנו שאנחנו מסוגלות
                  להכיל אותו.
                </p>
                <div className="bg-rose-50 p-6 rounded-2xl border border-rose-100 mt-4" data-id="element-2622" data-mp="2z0chmmkvfns3">
                  <p className="text-stone-800 font-medium italic" data-id="element-2623" data-mp="2kb6kd45lg7mu">
                    "ההתמודדות עם החתירה בכנרת והיכולת לנשום בתוך הקור, לצאת
                    ממצב של סטרס ולהרפות אל תוך הקושי — היא בדיוק אותה היכולת
                    שתשרת אותנו מול אתגרי היום-יום. כשאנחנו מתמודדות עם אתגר חדש
                    ומנצחות את הקרח - אנחנו מגלות את העוצמה האמיתית שלנו, וכוחות
                    שקיימים בנו ולא הכרנו."
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} className="grid grid-cols-2 gap-4" data-id="element-2624" data-mp="37zpvdrg12yim">
              <div className="space-y-4 mt-8" data-id="element-2625" data-mp="9o4fi7fkzmr0">
                <div className="rounded-3xl overflow-hidden shadow-lg aspect-[4/5]" data-id="element-2626" data-mp="no5h5p3o6y7j">
                  <video src="https://cdn.magicpatterns.com/uploads/o4YnUbrq6uHfsVbqHW7rax/IMG_5753.MOV" autoPlay muted loop playsInline className="w-full h-full object-cover" data-id="element-2627" data-mp="nm2pz1qs6z18" />
                </div>
                <div className="rounded-3xl overflow-hidden shadow-lg aspect-square" data-id="element-2628" data-mp="4pt43s9n146o">
                  <img src="https://cdn.magicpatterns.com/uploads/sFwj9YB7rvdyCPXtDrtWRm/IMG_5724.jpg" alt="קבוצת נשים בבוקר נשי" className="w-full h-full object-cover" data-id="element-2629" data-mp="3w3p4k7hntmeo" />
                </div>
              </div>
              <div className="space-y-4" data-id="element-2630" data-mp="17cokby83pyd5">
                <div className="rounded-3xl overflow-hidden shadow-lg aspect-square" data-id="element-2631" data-mp="17bn6xn3m4zs9">
                  <img src="https://cdn.magicpatterns.com/uploads/eom8E644yCYiU5JAxE9kj9/IMG_5754.jpg" alt="אישה באמבטיית קרח" className="w-full h-full object-cover" data-id="element-2632" data-mp="1cagez004ryl1" />
                </div>
                <div className="rounded-3xl overflow-hidden shadow-lg aspect-[4/5]" data-id="element-2633" data-mp="odsu0xzd644l">
                  <video src="https://cdn.magicpatterns.com/uploads/9XaBZJLpMsWrm3PxooNfNK/IMG_5728.MOV" autoPlay muted loop playsInline className="w-full h-full object-cover" data-id="element-2634" data-mp="tcnl0n7hiyzs" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* For Who */}
      <section className="py-24 px-4 bg-white" data-id="element-2635" data-mp="2eoxbfvh4112b">
        <div className="max-w-4xl mx-auto" data-id="element-2636" data-mp="2ert9rkwvdrra">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-12" data-id="element-2637" data-mp="31ljt2vnbl4l">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4" data-id="element-2638" data-mp="3pg4x0w8vwu9h">
              הבוקר הזה הוא בשבילך אם...
            </h2>
            <div className="w-24 h-1 bg-rose-400 mx-auto rounded-full" data-id="element-2639" data-mp="3fkjo0e5eetq8"></div>
          </motion.div>

          <motion.ul variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="space-y-4 max-w-2xl mx-auto" data-id="element-2640" data-mp="3bhx552qpgj56">
            {['את מרגישה שמגיע לך בוקר אחד רק בשבילך', 'תמיד רצית לנסות סאפ אבל לא ידעת איפה להתחיל', 'את מתגעגעת למפגש נשי אמיתי', 'את אוהבת מים, שמש ושקט', 'בא לך להיות במרחב נטול שיפוטיות - מהסביבה וגם משלך כלפי עצמך', 'את רוצה להשיב לעצמך את הביטחון בגוף שלך, ולגלות שיש בך עוצמות ויכולות להתמודד'].map((item, idx) => <motion.li key={idx} variants={fadeInUp} className="flex items-start gap-4 bg-rose-50/50 p-5 rounded-2xl border border-rose-100" data-id="element-2641" data-mp="3jmog1sqbnc96" data-mpi={idx}>
                <div className="bg-rose-400 rounded-full p-1 shrink-0 mt-1" data-id="element-2642" data-mp="39x4v8hj1t8bo">
                  <CheckCircle2 className="w-4 h-4 text-white" data-id="element-2643" data-mp="39v1vh5s3g0tr" />
                </div>
                <span className="text-lg text-stone-700 font-medium leading-relaxed" data-id="element-2644" data-mp="3eowd83o525q7">
                  {item}
                </span>
              </motion.li>)}
          </motion.ul>
        </div>
      </section>

      {/* Details */}
      <section className="py-24 px-4 bg-gradient-to-b from-orange-50 to-rose-100" data-id="element-2645" data-mp="2qxikhwm2s84p">
        <div className="max-w-5xl mx-auto" data-id="element-2646" data-mp="2vx3s0vfb9ot1">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-2647" data-mp="2vvsguajjderw">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4" data-id="element-2648" data-mp="2masv3w3w5tda">
              לוז הבוקר
            </h2>
            <div className="w-24 h-1 bg-rose-400 mx-auto rounded-full mb-6" data-id="element-2649" data-mp="2hcrdlfm4623x"></div>
            <div className="inline-flex flex-wrap items-center justify-center gap-2 text-sm" data-id="element-2650" data-mp="2v8czvf0yq7sn">
              <span className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-rose-200 text-stone-700 px-4 py-2 rounded-full shadow-sm" data-id="element-2651" data-mp="2r8c7srkxxy34">
                <MapPin className="w-4 h-4 text-rose-500" data-id="element-2652" data-mp="300mjyefecdn0" />
                <span className="font-medium" data-id="element-2653" data-mp="3038tgjlul37v">חוף מעגן, כנרת</span>
              </span>
              <span className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-rose-200 text-stone-700 px-4 py-2 rounded-full shadow-sm" data-id="element-2654" data-mp="3ew2kn2ahf7sd">
                <Clock className="w-4 h-4 text-rose-500" data-id="element-2655" data-mp="3h3lb53vrygrj" />
                <span className="font-medium" data-id="element-2656" data-mp="3m13mt216fa38">09:00 - 13:00</span>
              </span>
            </div>
            <div className="mt-5 flex flex-col items-center justify-center gap-1.5 text-sm" data-id="element-2657" data-mp="3r0q01bh08qc1">
              <span className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-2 rounded-full shadow-sm font-bold text-base" data-id="element-2658" data-mp="3vqwmp5ryi0rn">
                <Calendar className="w-4 h-4 text-emerald-500" data-id="element-2659" data-mp="4my6pvo03qhl" />
                נותרו מקומות בודדים לבוקר נשי ב-4.9.2026
              </span>
              <span className="text-stone-500 font-medium" data-id="element-2660" data-mp="dla6ooarc0mp">
                תאריך באוקטובר יפורסם בקרוב
              </span>
            </div>
          </motion.div>

          <div className="max-w-3xl mx-auto mb-16" data-id="element-2661" data-mp="iiaccy3p31da">
            <div className="relative" data-id="element-2662" data-mp="2fe84yznazlf1">
              {/* Vertical timeline connector */}
              <div className="absolute right-6 md:right-8 top-3 bottom-3 w-px bg-gradient-to-b from-rose-300 via-rose-300 to-orange-300" data-id="element-2663" data-mp="2kd2l1ttvyvvg"></div>

              <ul className="space-y-6" data-id="element-2664" data-mp="2cul97gyjf4zb">
                {[{
                time: '09:00',
                title: 'מעגל פתיחה',
                desc: 'מפגש ראשון על החוף, היכרות וכוונה לבוקר'
              }, {
                time: '10:00',
                title: 'סאפ בכנרת',
                desc: 'הדרכה מקצועית למתחילות וגלישה רגועה על המים'
              }, {
                time: '11:30',
                title: 'בראנץ׳ נשי',
                desc: 'ארוחה מפנקת, יין טוב, שיחה אמיתית וסגירת מעגל'
              }, {
                time: '12:30',
                title: 'הדרכת נשימות ואמבטיית קרח',
                desc: 'חיבור למערכת הנשימה, ויסות סטרס וטבילה מעצימה במי קרח'
              }].map((item, idx) => {
                return <motion.li key={idx} initial={{
                  opacity: 0,
                  x: 20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: idx * 0.1,
                  duration: 0.5
                }} className="relative flex items-start gap-4 md:gap-6" data-id="element-2665" data-mp="1v32dv131tlg6" data-mpi={idx}>
                      {/* Time marker */}
                      <div className="relative z-10 shrink-0" data-id="element-2666" data-mp="24zfgwpkhhbnl">
                        <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white flex flex-col items-center justify-center shadow-md ring-4 ring-rose-100 border border-rose-200" data-id="element-2667" data-mp="291it5gez8k9x">
                          <span className="text-rose-600 font-bold text-sm md:text-base leading-none" data-id="element-2668" data-mp="31k69kij13b4o">
                            {item.time}
                          </span>
                        </div>
                      </div>

                      {/* Content card */}
                      <div className="flex-1 bg-white rounded-2xl p-5 md:p-6 border border-rose-100 shadow-sm hover:shadow-md transition-shadow" data-id="element-2669" data-mp="zui9y83scolf">
                        <h3 className="text-lg md:text-xl font-bold text-stone-900 mb-2" data-id="element-2670" data-mp="uvnu9f147g5j">
                          {item.title}
                        </h3>
                        <p className="text-stone-600 leading-relaxed" data-id="element-2671" data-mp="2703x51ie0l4">
                          {item.desc}
                        </p>
                      </div>
                    </motion.li>;
              })}
              </ul>
            </div>
          </div>

          {/* Price Card */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-2xl mx-auto" data-id="element-2672" data-mp="1shl7oy0n0vam">
            <div className="relative rounded-3xl overflow-hidden shadow-xl shadow-rose-300/30 border border-rose-200 bg-gradient-to-br from-rose-100 via-rose-50 to-orange-50" data-id="element-2673" data-mp="39cvr0ihh5vo6">
              <div className="relative z-10 p-8 md:p-12 text-center" data-id="element-2674" data-mp="3j38cqe10zuvw">
                <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur-sm border border-rose-200 px-4 py-1.5 rounded-full mb-6 text-rose-600" data-id="element-2675" data-mp="2vdw2datypgs0">
                  <Users className="w-4 h-4" data-id="element-2676" data-mp="2qgnmjgwadotx" />
                  <span className="text-sm font-medium" data-id="element-2677" data-mp="34f3rb2al959f">מקומות מוגבלים</span>
                </div>

                <div className="text-rose-500 text-lg mb-3 font-medium" data-id="element-2678" data-mp="2zezlc67b3sxg">
                  השקעה בבוקר שלך
                </div>
                <div className="flex items-baseline justify-center gap-2 mb-2" data-id="element-2679" data-mp="jn1yen78zt8y">
                  <span className="text-6xl md:text-7xl font-extrabold text-rose-700" data-id="element-2680" data-mp="eq1sqdeb8sid">
                    ₪480
                  </span>
                </div>
                <p className="text-stone-600 text-sm mb-10" data-id="element-2681" data-mp="n8n9r4irlj1w">
                  לאישה • כולל סאפ, הדרכה, אמבטיית קרח ובראנץ׳
                </p>

                <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 mb-8 text-right border border-rose-100" data-id="element-2682" data-mp="d8wgf65xxa3e">
                  <h4 className="font-bold mb-4 text-rose-600 text-center" data-id="element-2683" data-mp="i7z9rtck5rvj">
                    מה כלול במחיר:
                  </h4>
                  <ul className="space-y-3" data-id="element-2684" data-mp="15z4og56rie49">
                    {['הדרכת סאפ מקצועית למתחילות', 'השכרת ציוד מלא (סאפ, משוט, חגורה)', 'הדרכת נשימות ואמבטיית קרח', 'בראנץ׳ עשיר בסוף הבוקר', 'קפה ופינוקים לאורך הבוקר'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-2685" data-mp="1ou1uznl6s7fb" data-mpi={i}>
                        <CheckCircle2 className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" data-id="element-2686" data-mp="1tmmi669l7m9v" />
                        <span className="text-stone-700 font-medium" data-id="element-2687" data-mp="23iv1ig8mvzq7">
                          {item}
                        </span>
                      </li>)}
                  </ul>
                </div>

                <motion.button type="button" onClick={openForm} animate={{
                scale: [1, 1.03, 1]
              }} transition={{
                duration: 2.2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="group flex items-center justify-center gap-2.5 w-full bg-rose-500 hover:bg-rose-600 text-white text-xl md:text-2xl font-extrabold py-5 rounded-2xl transition-colors shadow-2xl shadow-rose-400/60 ring-4 ring-rose-200/70 focus:outline-none focus-visible:ring-4 focus-visible:ring-rose-300" data-id="element-2688" data-mp="1tiug8or7f9jd">
                  <Sparkles className="w-6 h-6 shrink-0" data-id="element-2689" data-mp="wuy0tn7i6k9q" />
                  <span data-id="element-2690" data-mp="38h8f24ohb1mk">להרשמה לבוקר הנשי</span>
                  <span className="transition-transform duration-200 group-hover:-translate-x-1" data-id="element-2691" data-mp="38gglt0ictfzg">
                    ←
                  </span>
                </motion.button>
                <p className="text-stone-500 text-sm mt-4 font-medium" data-id="element-2692" data-mp="3hhokvksp2kul">
                  השארת פרטים והמשך לתשלום מאובטח
                </p>
              </div>
            </div>
          </motion.div>

          {/* Questions / WhatsApp CTA */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-2xl mx-auto mt-10" data-id="element-2693" data-mp="2pslb5pi1giak">
            <div className="bg-white border border-rose-100 rounded-3xl p-8 text-center shadow-sm" data-id="element-2694" data-mp="262rgn3nxvp2n">
              <div className="inline-flex items-center gap-2 mb-3 text-rose-500" data-id="element-2695" data-mp="2b1un8sjki3cq">
                <Heart className="w-5 h-5" data-id="element-2696" data-mp="2azrljb91yls6" />
                <span className="text-xs font-semibold tracking-wider uppercase" data-id="element-2697" data-mp="hic7qnf49mjl">
                  אני כאן בשבילך
                </span>
                <Heart className="w-5 h-5" data-id="element-2698" data-mp="clvjzubtxa71" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-stone-900 mb-3 leading-tight" data-id="element-2699" data-mp="qbp5q3s1fecj">
                מתלבטת? יש לך עוד שאלות?
              </h3>
              <p className="text-stone-600 text-lg mb-7 leading-relaxed" data-id="element-2700" data-mp="liw1dh0he80z">
                תכתבי לי בוואטסאפ ואשמח לדבר ולענות על כל שאלה 💌
              </p>
              <a href="https://wa.me/972548800260?text=היי%20עדי,%20יש%20לי%20כמה%20שאלות%20על%20הבוקר%20הנשי%20-%20הכוח%20שבמים" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#20BA5A] text-white font-bold py-4 px-8 rounded-full transition-all shadow-lg shadow-emerald-300/40 hover:scale-105" data-id="element-2701" data-mp="v9qosopgrdo0">
                <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" xmlns="http://www.w3.org/2000/svg" data-id="element-2702" data-mp="2ww4h6v5qe692">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" data-id="element-2703" data-mp="2n74ilcv55cve" />
                </svg>
                תכתבי לי בוואטסאפ
              </a>
            </div>
          </motion.div>

          {/* Cancellation Policy */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-2xl mx-auto mt-10" data-id="element-2704" data-mp="jowkfcqyz7ky">
            <div className="bg-white border border-rose-100 rounded-3xl p-8 shadow-sm text-right" data-id="element-2705" data-mp="17g26i9ni6w3p">
              <div className="flex items-center justify-center gap-2 mb-5 text-rose-500" data-id="element-2706" data-mp="12n925mvy5ps5">
                <Info className="w-5 h-5" data-id="element-2707" data-mp="1a0i4qh473fvr" />
                <span className="text-xs font-semibold tracking-wider uppercase" data-id="element-2708" data-mp="1a1jiitcligh6">
                  מדיניות ביטולים
                </span>
              </div>
              <ul className="space-y-3" data-id="element-2709" data-mp="1jwl3re3r2yeh">
                {[{
                when: 'ביטול עד 3 ימים לפני המועד',
                detail: 'חיוב של 25% מעלות הבוקר'
              }, {
                when: 'ביטול יום לפני המועד',
                detail: 'חיוב של 50% מעלות הבוקר'
              }, {
                when: 'ביטול ב-24 השעות שלפני האירוע',
                detail: 'ללא זיכוי'
              }].map((item, i) => <li key={i} className="flex items-start gap-3 bg-rose-50/60 border border-rose-100 rounded-2xl p-4" data-id="element-2710" data-mp="27p9z3hnivxpi" data-mpi={i}>
                    <Clock className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" data-id="element-2711" data-mp="2goewulr9fn0m" />
                    <div data-id="element-2712" data-mp="31xson2aqfcn8">
                      <span className="block font-bold text-stone-800" data-id="element-2713" data-mp="31x0rvo5ef8vd">
                        {item.when}
                      </span>
                      <span className="block text-stone-600 text-sm mt-0.5" data-id="element-2714" data-mp="3bwrmrqxvfpvz">
                        {item.detail}
                      </span>
                    </div>
                  </li>)}
              </ul>
              <p className="text-stone-500 text-xs text-center mt-5 leading-relaxed" data-id="element-2715" data-mp="2j7kmameuwq83">
                בהרשמה ובתשלום את מאשרת את מדיניות הביטולים המפורטת מעלה.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-900 text-stone-400 py-12 text-center border-t border-stone-800" data-id="element-2716" data-mp="2o7fh311r5zyf">
        <div className="max-w-4xl mx-auto px-4" data-id="element-2717" data-mp="20huso2mtyahr">
          <h2 className="text-2xl font-bold text-white mb-3" data-id="element-2718" data-mp="20gjpo517l6rp">
            בוקר נשי - הכוח שבמים
          </h2>
          <p className="mb-6 text-rose-200/80" data-id="element-2719" data-mp="1vihx8thw7v3k">
            ארבע שעות שיחזירו אותך אל עצמך.
          </p>
          <div className="flex flex-wrap justify-center gap-6 mb-8 text-sm" data-id="element-2720" data-mp="azedyiry2dsp">
            <span className="flex items-center gap-2" data-id="element-2721" data-mp="fs7lfeess08h">
              <Calendar size={16} data-id="element-2722" data-mp="20ayrpvweijh" /> 04.09.2026
            </span>
            <span className="flex items-center gap-2" data-id="element-2723" data-mp="1xy5igkowlr4">
              <Clock size={16} data-id="element-2724" data-mp="60uwaukdvo65" /> 09:00 - 13:00
            </span>
            <span className="flex items-center gap-2" data-id="element-2725" data-mp="tr7a2m2b8f4n">
              <MapPin size={16} data-id="element-2726" data-mp="trqh37qf9j3w" /> חוף מעגן
            </span>
          </div>
          <p className="text-sm opacity-60" data-id="element-2727" data-mp="yqjxb7x3fo7r">© 2026 כל הזכויות שמורות.</p>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a href="https://wa.me/972548800260?text=היי,%20אני%20רוצה%20להירשם%20לבוקר%20הנשי%20-%20הכוח%20שבמים" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 left-6 z-50 bg-[#25D366] text-white p-4 rounded-full shadow-lg hover:scale-110 hover:shadow-xl transition-all duration-300 flex items-center justify-center group" aria-label="Contact on WhatsApp" data-id="element-2728" data-mp="kq90zpd0lhf7">
        <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" xmlns="http://www.w3.org/2000/svg" data-id="element-2729" data-mp="2dhp6jdyxohss">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" data-id="element-2730" data-mp="2ihjw9e6z31c9" />
        </svg>
      </a>

      {/* Registration Modal */}
      <AnimatePresence data-id="element-2731" data-mp="kmefpt9t1274">
        {isFormOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={closeForm} className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{
        backgroundColor: 'rgba(60, 20, 40, 0.55)',
        backdropFilter: 'blur(6px)'
      }} data-id="element-2732" data-mp="fud6kg7xnrlb">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          type: 'spring',
          damping: 22,
          stiffness: 280
        }} onClick={e => e.stopPropagation()} dir="rtl" className="relative bg-gradient-to-br from-rose-50 via-white to-orange-50 rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-rose-200" data-id="element-2733" data-mp="3t2zj8ujo332u">
              {/* Close */}
              <button type="button" onClick={closeForm} disabled={isSubmitting} className="absolute top-4 left-4 text-stone-400 hover:text-stone-700 transition-colors p-2 rounded-full hover:bg-white/70 z-10 disabled:opacity-50" aria-label="סגירה" data-id="element-2734" data-mp="ox8wng4cb9x6">
                <X className="w-5 h-5" data-id="element-2735" data-mp="29e884xt9r82h" />
              </button>

              <div className="p-7 md:p-9" data-id="element-2736" data-mp="2o4fkcvsonuuq">
                {/* Header */}
                <div className="text-center mb-6" data-id="element-2737" data-mp="2o5qqv3dijgpj">
                  <div className="inline-flex items-center gap-2 mb-3 text-rose-500" data-id="element-2738" data-mp="2j65osk31qs8l">
                    <Heart className="w-5 h-5" data-id="element-2739" data-mp="1vgbcc3mn35ov" />
                    <span className="text-xs font-semibold tracking-wider uppercase" data-id="element-2740" data-mp="1vh393hrz39gq">
                      הרשמה
                    </span>
                    <Heart className="w-5 h-5" data-id="element-2741" data-mp="24g6s6lr53ov8" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-stone-900 mb-2" data-id="element-2742" data-mp="20ecwkrvsxb1x">
                    בוקר נשי - הכוח שבמים
                  </h3>
                  <p className="text-stone-600 text-sm" data-id="element-2743" data-mp="3ftse7y1pdacj">
                    השאירי פרטים ונמשיך לתשלום מאובטח
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4" data-id="element-2744" data-mp="ayk92u3y7a4w">
                  {/* Full name */}
                  <div data-id="element-2745" data-mp="ezck9swdrp3t">
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5" data-id="element-2746" data-mp="f0xefhes9615">
                      שם מלא <span className="text-rose-500" data-id="element-2747" data-mp="jxxkhsbmu8s9">*</span>
                    </label>
                    <div className="relative" data-id="element-2748" data-mp="oybuhfwsnssx">
                      <User className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-rose-400 pointer-events-none" data-id="element-2749" data-mp="ovz5iismci6d" />
                      <input type="text" name="fullName" value={formData.fullName} onChange={handleFormChange} required disabled={isSubmitting} placeholder="השם המלא שלך" className="w-full bg-white border border-rose-200 rounded-xl py-3 pr-10 pl-4 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 transition-all" data-id="element-2750" data-mp="xyi5mv50sgox" />
                    </div>
                  </div>

                  {/* Email */}
                  <div data-id="element-2751" data-mp="2x7rqdesxnv0p">
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5" data-id="element-2752" data-mp="2s6c63oykxsjd">
                      כתובת מייל <span className="text-rose-500" data-id="element-2753" data-mp="2n7r8zyemjoz5">*</span>
                    </label>
                    <div className="relative" data-id="element-2754" data-mp="3kv9mfmj8df9h">
                      <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-rose-400 pointer-events-none" data-id="element-2755" data-mp="3ktycf4z6zb9x" />
                      <input type="email" name="email" value={formData.email} onChange={handleFormChange} required disabled={isSubmitting} placeholder="name@example.com" dir="ltr" className="w-full bg-white border border-rose-200 rounded-xl py-3 pr-10 pl-4 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 transition-all text-right" data-id="element-2756" data-mp="3fy9hjey4aijy" />
                    </div>
                  </div>

                  {/* Phone */}
                  <div data-id="element-2757" data-mp="1s6s7zyhl0rpt">
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5" data-id="element-2758" data-mp="1s60csorwcw02">
                      מספר טלפון <span className="text-rose-500" data-id="element-2759" data-mp="1e6r6cgthtg9a">*</span>
                    </label>
                    <div className="relative" data-id="element-2760" data-mp="1i941yceizxsc">
                      <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-rose-400 pointer-events-none" data-id="element-2761" data-mp="1i9xczpkip9ga" />
                      <input type="tel" name="phone" value={formData.phone} onChange={handleFormChange} required disabled={isSubmitting} placeholder="050-1234567" dir="ltr" className="w-full bg-white border border-rose-200 rounded-xl py-3 pr-10 pl-4 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 transition-all text-right" data-id="element-2762" data-mp="197wg4tt74pf5" />
                    </div>
                  </div>

                  {/* Preferred event date */}
                  <div data-id="element-2763" data-mp="1aoa9bpp5o2kq">
                    <label className="block text-sm font-semibold text-stone-700 mb-2" data-id="element-2764" data-mp="1ans6ga7zmq3u">
                      לאיזה תאריך תרצי להצטרף?{' '}
                      <span className="text-rose-500" data-id="element-2765" data-mp="1kn97gecaofth">*</span>
                    </label>
                    <div className="grid grid-cols-1 gap-3" data-id="element-2766" data-mp="1kkws88xx8jk4">
                      {EVENT_DATES.map(opt => {
                    const active = formData.selectedDate === opt.value;
                    return <button key={opt.value} type="button" disabled={isSubmitting} onClick={() => setFormData(prev => ({
                      ...prev,
                      selectedDate: opt.value
                    }))} className={`flex items-center justify-center gap-2 py-3 px-2 rounded-xl font-bold text-sm border-2 transition-all ${active ? 'bg-rose-500 text-white border-rose-500 shadow-md shadow-rose-300/50' : 'bg-white text-stone-700 border-rose-200 hover:border-rose-400'}`} data-id="element-2767" data-mp="3hfswx5ff0mmq">
                            <Calendar className={`w-4 h-4 shrink-0 ${active ? 'text-white' : 'text-rose-400'}`} data-id="element-2768" data-mp="2mk4agd0wmm23" />
                            {opt.label}
                          </button>;
                  })}
                    </div>
                    <p className="mt-2 text-xs text-stone-500" data-id="element-2769" data-mp="5z4mfag05ong">
                      תאריך באוקטובר יפורסם בקרוב
                    </p>
                  </div>

                  {/* Date of birth */}
                  <div data-id="element-2770" data-mp="1mfv2b3eofk4p">
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5" data-id="element-2771" data-mp="1hmin1jvw0pom">
                      תאריך לידה <span className="text-rose-500" data-id="element-2772" data-mp="1co7gpj4swxb5">*</span>
                    </label>
                    <div className="relative" data-id="element-2773" data-mp="17ocptfl107q3">
                      <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-rose-400 pointer-events-none" data-id="element-2774" data-mp="17q5y0zbaf007" />
                      <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleFormChange} required disabled={isSubmitting} dir="ltr" className="w-full bg-white border border-rose-200 rounded-xl py-3 pr-10 pl-4 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 transition-all text-right" data-id="element-2775" data-mp="12runsnh2ynhg" />
                    </div>
                  </div>

                  {/* No medical limitations checkbox */}
                  <div data-id="element-2776" data-mp="37l9nbid8g5l1">
                    <label className={`flex items-start gap-3 cursor-pointer p-4 rounded-xl border-2 transition-all ${formData.noMedicalLimitations ? 'bg-rose-50 border-rose-400' : 'bg-white border-rose-200 hover:border-rose-300'} ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`} data-id="element-2777" data-mp="32p1c2g25owj5">
                      <input type="checkbox" name="noMedicalLimitations" checked={formData.noMedicalLimitations} onChange={e => setFormData(prev => ({
                    ...prev,
                    noMedicalLimitations: e.target.checked
                  }))} disabled={isSubmitting} required className="sr-only" data-id="element-2778" data-mp="2opjjrqpnj151" />
                      <div className={`shrink-0 mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${formData.noMedicalLimitations ? 'bg-rose-500 border-rose-500' : 'bg-white border-rose-300'}`} data-id="element-2779" data-mp="z8nxnqxisdb7">
                        {formData.noMedicalLimitations && <CheckCircle2 className="w-4 h-4 text-white" strokeWidth={3} data-id="element-2780" data-mp="bk4qjeq7i6kp" />}
                      </div>
                      <div className="flex-1" data-id="element-2781" data-mp="1rwac5u8ad2cu">
                        <span className="block text-sm font-semibold text-stone-800" data-id="element-2782" data-mp="1rvihclmij8nm">
                          אני מאשרת שאין לי מגבלות רפואיות{' '}
                          <span className="text-rose-500" data-id="element-2783" data-mp="3syja1sqxr29i">*</span>
                        </span>
                        <span className="block text-xs text-stone-500 mt-0.5" data-id="element-2784" data-mp="3nzf1n4bu58k8">
                          המאפשרות לי להשתתף בפעילות סאפ במים
                        </span>
                      </div>
                    </label>
                  </div>

                  {/* SUP experience yes/no */}
                  <div data-id="element-2785" data-mp="3a81lm0d4ysty">
                    <label className="block text-sm font-semibold text-stone-700 mb-2" data-id="element-2786" data-mp="3a7ib34ptf6py">
                      האם יש לך ניסיון על סאפ?{' '}
                      <span className="text-rose-500" data-id="element-2787" data-mp="35arzatl1mxac">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3" data-id="element-2788" data-mp="3j1wr59i66la4">
                      {[{
                    value: 'yes',
                    label: 'כן'
                  }, {
                    value: 'no',
                    label: 'לא'
                  }].map(opt => {
                    const active = formData.hasExperience === opt.value;
                    return <button key={opt.value} type="button" disabled={isSubmitting} onClick={() => setFormData(prev => ({
                      ...prev,
                      hasExperience: opt.value
                    }))} className={`py-3 rounded-xl font-bold text-sm border-2 transition-all ${active ? 'bg-rose-500 text-white border-rose-500 shadow-md shadow-rose-300/50' : 'bg-white text-stone-700 border-rose-200 hover:border-rose-400'}`} data-id="element-2789" data-mp="2h6sbp8io06ur">
                            {opt.label}
                          </button>;
                  })}
                    </div>
                  </div>

                  {/* Notes / sensitivities */}
                  <div data-id="element-2790" data-mp="1uzpz41yz4eac">
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5" data-id="element-2791" data-mp="1q6n19hn5yioc">
                      הערות / רגישויות שחשוב שנכיר
                    </label>
                    <textarea name="notes" value={formData.notes} onChange={handleFormChange} disabled={isSubmitting} rows={3} placeholder="אלרגיות, רגישויות תזונה, כל דבר שחשוב לי לדעת..." className="w-full bg-white border border-rose-200 rounded-xl py-3 px-4 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 transition-all resize-none" data-id="element-2792" data-mp="1l5qyj7ejt2ki" />
                  </div>

                  {/* Submit */}
                  <button type="submit" disabled={isSubmitting} className="w-full bg-rose-500 hover:bg-rose-600 disabled:opacity-70 disabled:cursor-not-allowed text-white text-lg font-bold py-4 rounded-2xl transition-all shadow-lg shadow-rose-300/60 hover:scale-[1.01] mt-2" data-id="element-2793" data-mp="njtjjjzdnfdb">
                    {isSubmitting ? 'פותחת את חלון התשלום…' : 'המשך לתשלום ←'}
                  </button>

                  <p className="text-stone-500 text-xs text-center pt-1" data-id="element-2794" data-mp="3dye3wuk1iwse">
                    התשלום מאובטח ומבוצע דרך Tranzila
                  </p>

                  {/* Cancellation policy note */}
                  <div className="bg-rose-50/70 border border-rose-100 rounded-xl p-3 text-right" data-id="element-2795" data-mp="3mz3xrspqqd3h">
                    <div className="flex items-center gap-1.5 mb-1 text-rose-500" data-id="element-2796" data-mp="3ruj9of46tyx9">
                      <Info className="w-3.5 h-3.5" data-id="element-2797" data-mp="nykz21lv33e" />
                      <span className="text-[11px] font-bold" data-id="element-2798" data-mp="1xj4iruzfxtfx">
                        מדיניות ביטולים
                      </span>
                    </div>
                    <p className="text-stone-500 text-[11px] leading-relaxed" data-id="element-2799" data-mp="22j8xbfgsge3i">
                      ביטול עד 3 ימים לפני — חיוב 25% · ביטול יום לפני — חיוב
                      50% · ביטול ב-24 השעות שלפני האירוע — ללא זיכוי.
                    </p>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Payment Modal (Tranzila iframe) */}
      <AnimatePresence data-id="element-2800" data-mp="2despnorq44pi">
        {isPaymentOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{
        backgroundColor: 'rgba(60, 20, 40, 0.65)',
        backdropFilter: 'blur(6px)'
      }} onClick={closePayment} data-id="element-2801" data-mp="2dd7upy1hyjr4">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          type: 'spring',
          damping: 22,
          stiffness: 280
        }} onClick={e => e.stopPropagation()} dir="rtl" className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden border border-rose-200 flex flex-col" data-id="element-2802" data-mp="2s6bf0d6h3blj">
              {/* Header */}
              <div className="bg-gradient-to-l from-rose-50 to-orange-50 px-6 py-4 border-b border-rose-100 flex items-center justify-between shrink-0" data-id="element-2803" data-mp="2o17iscu9tpgi">
                <div className="flex items-center gap-2 text-stone-700" data-id="element-2804" data-mp="2e28jhkveb666">
                  {paymentSuccess ? <>
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-2805" data-mp="2tj8agm2gv78q" />
                      <span className="text-sm font-semibold" data-id="element-2806" data-mp="2pioazbuf7rjf">
                        ההרשמה הושלמה
                      </span>
                    </> : <>
                      <Lock className="w-4 h-4 text-rose-500" data-id="element-2807" data-mp="2fljlb21d9dxv" />
                      <span className="text-sm font-semibold" data-id="element-2808" data-mp="2fn4kj4ymll22">
                        תשלום מאובטח באמצעות טרנזילה
                      </span>
                    </>}
                </div>
                <div className="flex items-center gap-4" data-id="element-2809" data-mp="21td8npxf9p36">
                  {!paymentSuccess && <div className="text-sm font-bold text-rose-600 hidden sm:block" data-id="element-2810" data-mp="1wu8vypbah983">
                      בוקר נשי • ₪{PRICE}
                    </div>}
                  <button type="button" onClick={closePayment} className="text-stone-400 hover:text-stone-700 transition-colors p-1.5 rounded-full hover:bg-white/70" aria-label="סגירת חלון התשלום" data-id="element-2811" data-mp="1rxa4yfmx9ie4">
                    <X className="w-5 h-5" data-id="element-2812" data-mp="1htbxfh9dq5yl" />
                  </button>
                </div>
              </div>

              {/* Body — either iframe or success state */}
              {paymentSuccess ? <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex-1 bg-gradient-to-br from-rose-50 via-white to-orange-50 px-8 py-12 text-center flex flex-col items-center justify-center" data-id="element-2813" data-mp="1rn2iec6ary9j">
                  <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              damping: 14,
              stiffness: 200,
              delay: 0.1
            }} className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-6 ring-8 ring-emerald-50" data-id="element-2814" data-mp="bbez9b1h4cxr">
                    <CheckCircle2 className="w-10 h-10 text-emerald-500" data-id="element-2815" data-mp="3fokj411fkrzi" />
                  </motion.div>
                  <h3 className="text-2xl md:text-3xl font-bold text-stone-900 mb-3" data-id="element-2816" data-mp="31xfppgonovxm">
                    ההרשמה הצליחה! 🌸
                  </h3>
                  <p className="text-stone-600 text-base md:text-lg leading-relaxed max-w-md mb-2" data-id="element-2817" data-mp="2s12w0it42i2v">
                    קיבלנו את ההרשמה ואת התשלום שלך לבוקר הנשי.
                  </p>
                  <p className="text-stone-600 text-base md:text-lg leading-relaxed max-w-md mb-8" data-id="element-2818" data-mp="2j04q1uz440hn">
                    אשלח אלייך פרטים נוספים בוואטסאפ בקרוב 💌
                  </p>

                  <div className="bg-white border border-rose-100 rounded-2xl p-5 max-w-sm w-full mb-6 text-right space-y-2" data-id="element-2819" data-mp="2n3aserj3t7iu">
                    <div className="flex items-start gap-2 text-stone-700" data-id="element-2820" data-mp="1zembvxdu7q9i">
                      <Calendar className="w-4 h-4 text-rose-500 mt-1 shrink-0" data-id="element-2821" data-mp="2dduez1d4fiiz" />
                      <span className="font-medium" data-id="element-2822" data-mp="2dbrgru1tej36">
                        שישי, 04.09.2026
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-stone-700" data-id="element-2823" data-mp="2nb8i5za1aatc">
                      <Clock className="w-4 h-4 text-rose-500" data-id="element-2824" data-mp="2id709hocghjg" />
                      <span className="font-medium" data-id="element-2825" data-mp="2icoyy6mtrd4o">09:00 - 13:00</span>
                    </div>
                    <div className="flex items-center gap-2 text-stone-700" data-id="element-2826" data-mp="2w3k71gpi1q77">
                      <MapPin className="w-4 h-4 text-rose-500" data-id="element-2827" data-mp="2s41nopdbtn6h" />
                      <span className="font-medium" data-id="element-2828" data-mp="362003p8379jc">חוף מעגן, כנרת</span>
                    </div>
                  </div>

                  <button type="button" onClick={closePayment} className="bg-rose-500 hover:bg-rose-600 text-white font-bold px-8 py-3 rounded-full transition-all shadow-lg shadow-rose-300/50 hover:scale-[1.02]" data-id="element-2829" data-mp="314hots6lki86">
                    סיום
                  </button>
                </motion.div> : <>
                  <div className="flex-1 bg-rose-50/50 overflow-hidden" data-id="element-2830" data-mp="2cj01l18xk7of">
                    <iframe ref={paymentIframeRef} name="tranzila" width="100%" height="600" frameBorder="0" allow="payment" className="w-full h-[600px] max-h-[78vh] bg-white" title="חלון תשלום מאובטח" data-id="element-2831" data-mp="2hhb9h6fo0840"></iframe>
                  </div>
                  {/* Cancellation policy strip inside the payment window */}
                  <div className="shrink-0 bg-rose-50 border-t border-rose-100 px-5 py-3 text-right" data-id="element-2832" data-mp="113bca387ewkg">
                    <div className="flex items-center gap-1.5 mb-1 text-rose-500" data-id="element-2833" data-mp="tjslvbwms11u">
                      <Info className="w-3.5 h-3.5 shrink-0" data-id="element-2834" data-mp="orr3d88vde3d" />
                      <span className="text-[11px] font-bold" data-id="element-2835" data-mp="oq7qzsqc3n6v">
                        מדיניות ביטולים
                      </span>
                    </div>
                    <p className="text-stone-500 text-[11px] leading-relaxed" data-id="element-2836" data-mp="ev5wsih7buxf">
                      ביטול עד 3 ימים לפני — חיוב 25% · ביטול יום לפני — חיוב
                      50% · ביטול ב-24 השעות שלפני האירוע — ללא זיכוי.
                    </p>
                  </div>
                </>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}